/**
 * Authentication Context
 * Manages user authentication state across the app
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI } from '../services/api';
import * as socketService from '../services/socket';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check for existing session on mount
  useEffect(() => {
    const initAuth = async () => {
      const token = authAPI.getToken();
      if (token) {
        try {
          const data = await authAPI.getCurrentUser();
          setUser(data.user);
          socketService.connect();
        } catch (err) {
          console.error('Session restore failed:', err);
          authAPI.removeToken();
        }
      }
      setLoading(false);
    };

    initAuth();
  }, []);

  const register = useCallback(async (userData) => {
    setError(null);
    try {
      const data = await authAPI.register(userData);
      setUser(data.user);
      socketService.connect();
      return data;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  const login = useCallback(async (credentials) => {
    setError(null);
    try {
      const data = await authAPI.login(credentials);
      setUser(data.user);
      socketService.connect();
      return data;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  const socialLogin = useCallback(async (provider, token, extraData = {}) => {
    setError(null);
    try {
      const data = await authAPI.socialLogin(provider, token, extraData);
      if (data.needs_completion) {
        return data; // Caller handles the completion flow
      }
      setUser(data.user);
      socketService.connect();
      return data;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await authAPI.logout();
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      setUser(null);
      socketService.disconnect();
    }
  }, []);

  const updateUser = useCallback((updates) => {
    setUser(prev => ({ ...prev, ...updates }));
  }, []);

  const value = {
    user,
    loading,
    error,
    isAuthenticated: !!user,
    isYachtOwner: user?.user_type === 'yacht_owner',
    isHelper: user?.user_type === 'helper',
    isAdmin: user?.user_type === 'admin',
    register,
    login,
    socialLogin,
    logout,
    updateUser,
    clearError: () => setError(null),
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
